<template>
  <div class="reporting-panel">
    <div class="reporting-layout">
      <aside class="reporting-aside">
        <article class="notice-card">
          <h3>举报须知</h3>
          <div v-if="pinnedNotice && pinnedNotice.bodyContent" class="rich-body" v-html="pinnedNotice.bodyContent" />
          <p v-else class="muted-copy">暂无须知正文，请在管理后台「党建文化 → 违法违纪举报」维护置顶文章。</p>
        </article>
        <div v-if="otherNotices.length" class="notice-list">
          <h4>其他须知</h4>
          <button
            v-for="item in otherNotices"
            :key="item.entryId"
            type="button"
            class="notice-item"
            @click="front.openCmsEntry(item)"
          >
            <strong>{{ item.title }}</strong>
            <span>{{ item.summary || item.date || '' }}</span>
          </button>
        </div>
        <FrontPagination
          :page-num="pageNum"
          :page-size="pageSize"
          :total="total"
          @change="onPageChange"
        />
        <div class="channel-card">
          <h4>其他举报渠道</h4>
          <p>邮寄、电话、邮箱等渠道信息请以学会公示为准；后续可由站点配置维护。</p>
        </div>
      </aside>

      <div class="reporting-main">
        <div v-if="submitted" class="success-panel">
          <h3>提交成功</h3>
          <p>感谢您的实名或匿名举报，纪检部门将按规定处理。请勿重复提交。</p>
          <button type="button" class="ghost-action" @click="resetForm">再写一份</button>
        </div>
        <form v-else class="report-form" @submit.prevent="onSubmit">
          <h3>在线举报</h3>
          <label>
            <span>被举报人姓名 <em>*</em></span>
            <input v-model.trim="form.reportedName" type="text" maxlength="100" required />
          </label>
          <label>
            <span>部门 / 支部</span>
            <input v-model.trim="form.reportedDept" type="text" maxlength="200" />
          </label>
          <label>
            <span>违规类型 <em>*</em></span>
            <select v-model="form.violationType" required>
              <option disabled value="">请选择</option>
              <option v-for="item in violationTypes" :key="item" :value="item">{{ item }}</option>
            </select>
          </label>
          <label>
            <span>事实与证据说明 <em>*</em></span>
            <textarea v-model.trim="form.description" rows="6" maxlength="1000" required />
            <small>{{ form.description.length }}/1000</small>
          </label>
          <label class="checkbox-row">
            <input v-model="form.isAnonymous" type="checkbox" true-value="1" false-value="0" />
            <span>匿名举报（默认开启）</span>
          </label>
          <template v-if="form.isAnonymous === '0'">
            <label>
              <span>举报人姓名 <em>*</em></span>
              <input v-model.trim="form.reporterName" type="text" maxlength="100" required />
            </label>
            <label>
              <span>联系方式 <em>*</em></span>
              <input v-model.trim="form.reporterContact" type="text" maxlength="200" required />
            </label>
          </template>
          <p v-if="errorMessage" class="form-error">{{ errorMessage }}</p>
          <button type="submit" class="primary-action" :disabled="submitting">
            {{ submitting ? '提交中…' : '提交举报' }}
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { listPublicCmsEntries, submitPartyReport, unwrapPublicPage, applyClientPageFallback } from '../../api/paleo'
import FrontPagination from '../common/FrontPagination.vue'

export default {
  name: 'ReportingSection',
  components: { FrontPagination },
  inject: ['front'],
  data() {
    return {
      loading: false,
      submitting: false,
      submitted: false,
      errorMessage: '',
      notices: [],
      pageNum: 1,
      pageSize: 5,
      total: 0,
      loadSeq: 0,
      violationTypes: ['科研经费违规', '学术诚信', '作风纪律', '其他'],
      form: {
        reportedName: '',
        reportedDept: '',
        violationType: '',
        description: '',
        isAnonymous: '1',
        reporterName: '',
        reporterContact: ''
      }
    }
  },
  computed: {
    pinnedNotice() {
      return this.notices.find(item => item.pinned === '1') || this.notices[0] || null
    },
    otherNotices() {
      if (!this.pinnedNotice) {
        return this.notices.slice(1)
      }
      return this.notices.filter(item => item.entryId !== this.pinnedNotice.entryId)
    }
  },
  created() {
    this.loadNotices()
  },
  methods: {
    onPageChange(page) {
      this.pageNum = page
      this.loadNotices()
    },
    async loadNotices() {
      this.loading = true
      const seq = ++this.loadSeq
      try {
        const result = await listPublicCmsEntries({
          moduleCode: 'party',
          columnCode: 'party_reporting',
          pageNum: this.pageNum,
          pageSize: this.pageSize
        })
        if (seq !== this.loadSeq) return
        const { rows, total } = unwrapPublicPage(result)
        const sorted = this.front.sortPartyArticlesByPublishTime(rows.map(item => this.front.mapCmsEntry(item)))
        const paged = applyClientPageFallback(sorted, total, this.pageNum, this.pageSize)
        this.notices = paged.rows
        this.total = paged.total
      } catch (error) {
        if (seq !== this.loadSeq) return
        if (!this.notices.length) {
          this.notices = []
          this.total = 0
        }
      } finally {
        if (seq === this.loadSeq) this.loading = false
      }
    },
    resetForm() {
      this.submitted = false
      this.errorMessage = ''
      this.form = {
        reportedName: '',
        reportedDept: '',
        violationType: '',
        description: '',
        isAnonymous: '1',
        reporterName: '',
        reporterContact: ''
      }
    },
    async onSubmit() {
      this.errorMessage = ''
      if (!this.form.reportedName || !this.form.violationType || !this.form.description) {
        this.errorMessage = '请完整填写必填项'
        return
      }
      if (this.form.isAnonymous === '0' && (!this.form.reporterName || !this.form.reporterContact)) {
        this.errorMessage = '实名举报请填写姓名与联系方式'
        return
      }
      this.submitting = true
      try {
        await submitPartyReport({
          reportedName: this.form.reportedName,
          reportedDept: this.form.reportedDept || null,
          violationType: this.form.violationType,
          description: this.form.description,
          isAnonymous: this.form.isAnonymous,
          reporterName: this.form.isAnonymous === '0' ? this.form.reporterName : null,
          reporterContact: this.form.isAnonymous === '0' ? this.form.reporterContact : null
        })
        this.submitted = true
      } catch (error) {
        this.errorMessage = (error && error.message) || '提交失败，请稍后重试'
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.reporting-panel {
  width: 100%;
  min-width: 0;
}

.reporting-layout {
  display: grid;
  grid-template-columns: minmax(240px, 0.85fr) minmax(0, 1.15fr);
  gap: 16px;
  align-items: start;
}

.notice-card,
.channel-card,
.report-form,
.success-panel {
  background: #fff;
  border: 1px solid #e5e1da;
  padding: 18px 20px;
}

.notice-card h3,
.report-form h3,
.success-panel h3 {
  margin: 0 0 12px;
  color: #002b49;
  font-size: 18px;
}

.notice-list {
  margin-top: 12px;
}

.notice-list h4,
.channel-card h4 {
  margin: 0 0 8px;
  color: #c41e3a;
  font-size: 14px;
}

.channel-card {
  margin-top: 12px;
}

.notice-item {
  display: block;
  width: 100%;
  text-align: left;
  margin-bottom: 8px;
  padding: 10px 12px;
  border: 1px solid #e5e1da;
  background: #fcfaf7;
  cursor: pointer;
}

.notice-item strong {
  display: block;
  color: #002b49;
}

.notice-item span,
.muted-copy,
.channel-card p,
.success-panel p {
  color: #666;
  font-size: 13px;
  line-height: 1.6;
}

.report-form label {
  display: block;
  margin-bottom: 14px;
}

.report-form label > span {
  display: block;
  margin-bottom: 6px;
  color: #333;
  font-size: 13px;
}

.report-form em {
  color: #c41e3a;
  font-style: normal;
}

.report-form input,
.report-form select,
.report-form textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #d4d0c8;
  padding: 8px 10px;
  font: inherit;
  background: #fff;
}

.checkbox-row {
  display: flex !important;
  align-items: center;
  gap: 8px;
}

.checkbox-row input {
  width: auto;
}

.form-error {
  color: #c41e3a;
  margin: 0 0 12px;
}

.primary-action,
.ghost-action {
  border: 1px solid #c41e3a;
  background: #c41e3a;
  color: #fff;
  padding: 10px 18px;
  cursor: pointer;
}

.ghost-action {
  background: transparent;
  color: #c41e3a;
}

.primary-action:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.rich-body {
  font-size: 14px;
  line-height: 1.75;
  color: #333;
}

.rich-body >>> p,
.rich-body >>> div,
.rich-body >>> li {
  margin: 0 0 0.75em;
  line-height: 1.75;
}

.rich-body >>> p:last-child,
.rich-body >>> div:last-child {
  margin-bottom: 0;
}

@media (max-width: 900px) {
  .reporting-layout {
    grid-template-columns: 1fr;
  }
}
</style>
